import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type Post = Database['public']['Tables']['posts']['Row'];
type PostInsert = Database['public']['Tables']['posts']['Insert'];

export interface PostWithProfile extends Post {
  profiles: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    company: string | null;
    role: string | null;
    headline: string | null;
  } | null;
  post_media?: {
    id: string;
    storage_path: string;
    file_name: string;
    file_type: string;
  }[];
}

const FEED_PAGE_SIZE = 100;

export function usePosts(cohortTag?: string) {
  return useQuery({
    queryKey: ['posts', cohortTag],
    queryFn: async (): Promise<PostWithProfile[]> => {
      // First get posts (exclude deleted and channel posts for feed)
      let postsQuery = supabase
        .from('posts')
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });

      if (cohortTag && cohortTag !== 'all' && cohortTag !== 'global') {
        postsQuery = postsQuery.eq('cohort_tag', cohortTag);
      }

      const { data: posts, error: postsError } = await postsQuery.limit(FEED_PAGE_SIZE);
      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(posts.map(p => p.user_id))];

      // Fetch profiles + media in parallel to reduce latency
      const postIds = posts.map(p => p.id);
      const [profilesRes, mediaRes] = await Promise.all([
        userIds.length > 0
          ? supabase
              .from('profiles')
              .select('id, user_id, full_name, avatar_url, company, role, headline')
              .in('user_id', userIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length > 0
          ? supabase
              .from('post_media')
              .select('id, post_id, storage_path, file_name, file_type')
              .in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (mediaRes.error) throw mediaRes.error;

      const profiles = profilesRes.data || [];
      const media = mediaRes.data || [];

      // Create maps
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]));
      const mediaMap = new Map<string, typeof media>();
      media?.forEach(m => {
        const existing = mediaMap.get(m.post_id) || [];
        existing.push(m);
        mediaMap.set(m.post_id, existing);
      });

      // Merge posts with profiles and media
      return posts.map((post) => {
        const profile = profileMap.get(post.user_id);
        return {
          ...post,
          profiles: profile
            ? {
                id: profile.id,
                full_name: profile.full_name,
                avatar_url: profile.avatar_url,
                company: profile.company,
                role: profile.role,
                headline: profile.headline,
              }
            : null,
          post_media: mediaMap.get(post.id) || [],
        };
      });
    },
    staleTime: 1000 * 30,
  });
}

export function usePostsPaged(params: {
  cohortTag?: string;
  page: number;
  pageSize: number;
}) {
  return useQuery({
    queryKey: ['posts', 'paged', params],
    queryFn: async (): Promise<{ data: PostWithProfile[]; count: number }> => {
      const from = params.page * params.pageSize;
      const to = from + params.pageSize - 1;

      // First get posts (exclude deleted and channel posts for feed)
      let postsQuery = supabase
        .from('posts')
        .select('*', { count: 'exact' })
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (params.cohortTag && params.cohortTag !== 'all' && params.cohortTag !== 'global') {
        postsQuery = postsQuery.eq('cohort_tag', params.cohortTag);
      }

      const { data: posts, error: postsError, count } = await postsQuery;
      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return { data: [], count: count || 0 };

      // Get unique user IDs
      const userIds = [...new Set(posts.map(p => p.user_id))];

      // Fetch profiles + media in parallel to reduce latency
      const postIds = posts.map(p => p.id);
      const [profilesRes, mediaRes] = await Promise.all([
        userIds.length > 0
          ? supabase
              .from('profiles')
              .select('id, user_id, full_name, avatar_url, company, role, headline')
              .in('user_id', userIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length > 0
          ? supabase
              .from('post_media')
              .select('id, post_id, storage_path, file_name, file_type')
              .in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (mediaRes.error) throw mediaRes.error;

      const profiles = profilesRes.data || [];
      const media = mediaRes.data || [];

      // Create maps
      const profileMap = new Map(profiles.map(p => [p.user_id, p]));
      const mediaMap = new Map<string, typeof media>();
      media?.forEach(m => {
        const existing = mediaMap.get(m.post_id) || [];
        existing.push(m);
        mediaMap.set(m.post_id, existing);
      });

      // Merge posts with profiles and media
      const merged = posts.map((post) => {
        const profile = profileMap.get(post.user_id);
        return {
          ...post,
          profiles: profile
            ? {
                id: profile.id,
                full_name: profile.full_name,
                avatar_url: profile.avatar_url,
                company: profile.company,
                role: profile.role,
                headline: profile.headline,
              }
            : null,
          post_media: mediaMap.get(post.id) || [],
        };
      });

      return { data: merged, count: count || 0 };
    },
    staleTime: 1000 * 30,
  });
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (post: Omit<PostInsert, 'user_id'>) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('posts')
        .insert({ ...post, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, content, category }: { id: string; content: string; category: string }) => {
      const { data, error } = await supabase
        .from('posts')
        .update({ content, category })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['channel-posts'] });
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['channel-posts'] });
    },
  });
}
